import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { AccountDto, UserPlan } from "../../api/models/fraud.models";
import { ApiService } from "../../api/services/api.service";
import { catchError, finalize, Observable, of, shareReplay, Subject, Subscription, take, tap, throwError } from "rxjs";
import { Router } from '@angular/router';
import { VariablesService } from './variables.service';
import { environment } from '../../../../../environments/environment';
import { UserSocketService } from './user-socket.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorNavigationService } from './error-navigation.service';
import { DialogService } from './dialog.service';
import { AccountAccessService } from './account-access.service';

export const X_ACCOUNT_ID_KEY = 'app-x-account-Id';
export const LAST_URL_BEFORE_ACCOUNT_ERROR_KEY = 'app-last-url-before-account-error';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static readonly ACCOUNT_SWITCH_RETRY_DELAY_MS = 200;
  private static readonly ACCOUNT_SWITCH_MAX_ATTEMPTS = 8;
  private static readonly SUBSCRIPTION_EXPIRED_DIALOG_ID = 'subscription-expired-dialog';
  private static readonly ERROR_ROUTE_PREFIXES: readonly string[] = [
    '/choose-account',
    '/approve-change-account',
    '/access-denied',
    '/401',
    '/403',
    '/something-went-wrong',
  ];

  public readonly accountDto: WritableSignal<AccountDto | undefined> = signal<AccountDto | undefined>(undefined);
  public readonly accounts: WritableSignal<AccountDto[] | undefined> = signal<AccountDto[] | undefined>(undefined);
  public readonly refreshTrigger: WritableSignal<number> = signal(0);
  private readonly _apiService: ApiService = inject(ApiService);
  readonly variablesService: VariablesService = inject(VariablesService);
  private readonly _router: Router = inject(Router);
  private readonly _userSocketService: UserSocketService = inject(UserSocketService);
  private readonly _errorNavigationService: ErrorNavigationService = inject(ErrorNavigationService);
  private readonly _dialogService: DialogService = inject(DialogService);
  private _accountsRequested: boolean = false;
  private _accountsRequest$: Observable<AccountDto[]> | null = null;
  private _isUserSocketStarted: boolean = false;
  private _userSocketMessageSubscription: Subscription | null = null;
  private _userSocketErrorSubscription: Subscription | null = null;
  readonly effectiveUserPlan = computed<UserPlan | null>(() => this._resolveEffectiveUserPlan(this.accountDto()));

  accountSwitched$ = new Subject<string>();
  constructor() {
    this._initUserSocketHandlers();
  }

  get xAccountId(): string | null {
    return localStorage.getItem(X_ACCOUNT_ID_KEY);
  }

  set xAccountId(value: string) {
    localStorage.setItem(X_ACCOUNT_ID_KEY, value);
  }

  private _resolveEffectiveUserPlan(account: AccountDto | undefined): UserPlan | null {
    const plan = account?.userPlan ?? null;
    if (!plan) return null;
    if (plan.userPlanType !== 'BASIC') return plan;

    return this._resolveLastExpiredUserPlan(account) ?? plan;
  }

  private _resolveLastExpiredUserPlan(account: AccountDto | undefined): UserPlan | null {
    if (!account) return null;

    switch (account.accountType) {
      case 'PERSONAL':
        return account.lastExpiredUserPlan ?? null;
      case 'CORPORATE':
        return account.companyDto?.lastExpiredUserPlan ?? null;
      default:
        return null;
    }
  }

  getUser(): Observable<AccountDto | undefined> {
    return this._apiService.getUserByInitData().pipe(
      tap((user: AccountDto): void => {
        this._handleUserData(user);
      }),
      catchError((error: HttpErrorResponse) => {
        console.log('error', error);
        localStorage.getItem(X_ACCOUNT_ID_KEY) && localStorage.removeItem(X_ACCOUNT_ID_KEY);
        const isGroupChat = localStorage.getItem('isGroupChat');
        if (!isGroupChat) {
          return this._apiService.getUserByInitData().pipe(
            tap((user: AccountDto): void => {
              this._handleUserDataWithPersonal(user);
            }),
            catchError((errorMeRequest: HttpErrorResponse) => {
              switch (errorMeRequest.error.code) {
                case 33: {
                  this._saveLastUrl();
                  this._router.navigate(['/choose-account'], { queryParams: { type: 'blocked' } });
                  return of(undefined);
                }
                case 34: {
                  this._saveLastUrl();
                  this._router.navigate(['/choose-account'], { queryParams: { type: 'revoked' } });
                  return of(undefined);
                }
                case 43: {
                  this._router.navigate(['/403']);
                  return of(undefined);
                }
                case 44: {
                  this._router.navigate(['/access-denied']);
                  return of(undefined);
                }
                case 45: {
                  this._router.navigate(['/403']);
                  return of(undefined);
                }
                case 59: {
                  this._router.navigate(['/session-closed'], { queryParams: { reason: 'expired' } });
                  return of(undefined);
                }
                case 60: {
                  this._router.navigate(['/session-closed'], { queryParams: { reason: 'terminated' } });
                  return of(undefined);
                }
                default: {
                  return throwError(() => errorMeRequest);
                }
              }
            })
          );
        }
        switch (error.error.code) {
          case 33: {
            this._saveLastUrl();
            this._router.navigate(['/choose-account'], { queryParams: { type: 'blocked' } });
            return of(undefined);
          }
          case 34: {
            this._saveLastUrl();
            this._router.navigate(['/choose-account'], { queryParams: { type: 'revoked' } });
            return of(undefined);
          }
          case 43: {
            this._router.navigate(['/403']);
            return of(undefined);
          }
          case 44: {
            this._router.navigate(['/access-denied']);
            return of(undefined);
          }
          case 45: {
            this._router.navigate(['/403']);
            return of(undefined);
          }
          case 59: {
            this._router.navigate(['/session-closed'], { queryParams: { reason: 'expired' } });
            return of(undefined);
          }
          case 60: {
            this._router.navigate(['/session-closed'], { queryParams: { reason: 'terminated' } });
            return of(undefined);
          }
          default: {
            return throwError(() => error);
          }
        }
      })
    );
  }

  private _saveLastUrl(): void {
    const routerUrl = this._router.url;
    const browserUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const currentUrl = routerUrl && routerUrl !== '/' ? routerUrl : (browserUrl || '/');
    const isErrorRoute = AuthService.ERROR_ROUTE_PREFIXES.some((prefix) => currentUrl.startsWith(prefix));
    if (isErrorRoute) {
      return;
    }
    localStorage.setItem(LAST_URL_BEFORE_ACCOUNT_ERROR_KEY, currentUrl);
  }

  getAccounts(forceRefresh: boolean = false): Observable<AccountDto[]> {
    const cachedAccounts = this.accounts();
    if (!forceRefresh && cachedAccounts) {
      return of(cachedAccounts);
    }

    if (!forceRefresh && this._accountsRequest$) {
      return this._accountsRequest$;
    }

    const request$ = this._apiService.getAccounts().pipe(
      tap((accounts: AccountDto[]) => {
        this.accounts.set(accounts);
        this._accountsRequested = true;
      }),
      catchError((error) => {
        this._accountsRequested = false;
        this.accounts.set(undefined);
        return throwError(() => error);
      }),
      finalize(() => {
        this._accountsRequest$ = null;
      }),
      shareReplay(1)
    );

    this._accountsRequest$ = request$;
    return request$;
  }

  private _handleUserData(user: AccountDto): void {
    const newAccountId = user.user.activeTelegramAccountId ?? user.uuid;
    const oldAccountId = this.xAccountId;
    const accountChanged = !!oldAccountId && oldAccountId !== newAccountId;

    this.xAccountId = newAccountId;
    if (user.blocked) {
      this._saveLastUrl();
      this._router.navigate(['/choose-account'], { queryParams: { type: 'blocked' } });
      this._stopUserSocket();
      return;
    }
    if (user.isRevoked) {
      this._saveLastUrl();
      this._router.navigate(['/choose-account'], { queryParams: { type: 'revoked' } });
      this._stopUserSocket();
      return;
    }
    this.accountDto.set(user);
    if (accountChanged) {
      this._restartUserSocket();
    } else if (!this._isUserSocketStarted) {
      this._startUserSocket();
    }
    if (accountChanged) {
      this.accountSwitched$.next(newAccountId);
    }
    if (!this._accountsRequested) {
      this.getAccounts().pipe(take(1)).subscribe({
        next: () => {
        },
        error: () => {
        }
      });
    }
  }

  private _handleUserDataWithPersonal(user: AccountDto): void {
    this.accountDto.set(user);
    this._stopUserSocket();

    if (user.isRevoked) {
      this._saveLastUrl();
      this._router.navigate(['/choose-account'], { queryParams: { type: 'revoked' } });
      this._stopUserSocket();
      return;
    }

    this._saveLastUrl();
    this._router.navigate(['/choose-account'], { queryParams: { type: 'blocked' } });
  }

  refresh(): void {
    this.getUser().pipe(take(1)).subscribe({
      next: () => {
      }
    });
  }

  private _isExpectedAccount(user: AccountDto | undefined, expectedAccountId?: string): boolean {
    if (!expectedAccountId || !user) {
      return true;
    }

    return user.uuid === expectedAccountId;
  }

  changeAccount(expectedAccountId?: string, attempt: number = 0): void {
    this.variablesService.isProcessSwitchAccount.set(true);
    this.getUser().pipe(take(1)).subscribe({
      next: (user: AccountDto | undefined) => {
        const isExpectedAccount = this._isExpectedAccount(user, expectedAccountId);
        if (!isExpectedAccount && attempt < AuthService.ACCOUNT_SWITCH_MAX_ATTEMPTS) {
          setTimeout(
            () => this.changeAccount(expectedAccountId, attempt + 1),
            0
          );
          return;
        }

        if (!isExpectedAccount) {
          this.variablesService.isProcessSwitchAccount.set(false);
          return;
        }

        this._refreshPageData();
        if (user?.accountType === 'CORPORATE' && user.companyRole === 'VIEWER') {
          const currentUrl = this._router.url;
          if (!currentUrl.includes('/transactions') && !currentUrl.includes('/reports')) {
            this._router.navigate(['/subscribed-wallets']);
          }
        }
        if (user?.companyDto?.cluster) {
          const currentUrl = this._router.url;
          if (!currentUrl.includes('/reports')) {
            this._router.navigate(['/reports']);
          }
        }
        this.variablesService.isProcessSwitchAccount.set(false);
        console.log('user switch', user);
        setTimeout(() => {
          if (user && this._isExpectedAccount(user, expectedAccountId)) {
            this.isOpenExpireDialog(user);
          }
        }, 0);
      },
      error: () => {
        this.variablesService.isProcessSwitchAccount.set(false);
      }
    });
  }

  chooseAccountAfterBlockedAccount(uuid: string): void {
    this.xAccountId = uuid;
    this.accountDto.set(undefined);
    this._apiService.putActiveTelegramAccount(uuid).pipe(take(1)).subscribe({
      next: () => {
        this._apiService.getUserByInitData().pipe(take(1)).subscribe({
          next: (user: AccountDto) => {
            this.accountDto.set(user);
            this._startUserSocket();
            const lastUrlBeforeAccountError = localStorage.getItem(LAST_URL_BEFORE_ACCOUNT_ERROR_KEY);
            if (lastUrlBeforeAccountError) {
              this._router.navigateByUrl(lastUrlBeforeAccountError);
              localStorage.removeItem(LAST_URL_BEFORE_ACCOUNT_ERROR_KEY);
            } else {
              this._router.navigate(['/subscribed-wallets']);
            }
            console.log('user switch', user);
            setTimeout(() => {
              if (user) {
                this.isOpenExpireDialog(user);
              }
            }, 0);
          },
          error: (errorMeRequest: HttpErrorResponse) => {
            console.log('errorMeRequest', errorMeRequest);
            // if (errorMeRequest.error.code === 33 || errorMeRequest.error.code === 34) {
            //   this._saveLastUrl();
            // }
            this._errorNavigationService.handleHttpError(errorMeRequest);
          }
        });
      },
      error: (error: HttpErrorResponse) => {
        this._errorNavigationService.handleHttpError(error);
      }
    });
  }

  isOpenExpireDialog(user: AccountDto): void {
    console.log('user', user);
    if (!user) {
      return;
    }

    if (user?.userPlan.userPlanType === 'BASIC') {
      let lastUserPlan = false;
      switch (user?.accountType) {
        case 'PERSONAL':
          if (user?.lastExpiredUserPlan) {
            lastUserPlan = user?.paidFeatureLocked;
          }
          break;
        case 'CORPORATE':
          if (user?.companyDto?.lastExpiredUserPlan) {
            lastUserPlan = user?.companyDto?.paidFeatureLocked;
          }
          break;
      }
      if (lastUserPlan) {
        this.openSubscriptionExpiredDialog();
      } else {
        this._dialogService.getDialogById(AuthService.SUBSCRIPTION_EXPIRED_DIALOG_ID)?.close();
      }
    } else {
      this._dialogService.getDialogById(AuthService.SUBSCRIPTION_EXPIRED_DIALOG_ID)?.close();
    }
  }

  openSubscriptionExpiredDialog(): void {
    this._dialogService.openSubscriptionExpiredDialog(AuthService.SUBSCRIPTION_EXPIRED_DIALOG_ID).afterClosed().pipe(take(1)).subscribe({
      next: () => {
      },
    });
  }

  clearLocalStorage(): void {
    localStorage.removeItem('checkWalletFlow');
    localStorage.removeItem('draftChoosedProviders');
    localStorage.removeItem('checkWalletState');
    localStorage.removeItem('chooseNetworkData');
    localStorage.removeItem('checkWalletDataNetwork');
  }

  private _refreshPageData(): void {
    const currentUrl = this._router.url;

    // Определяем текущую страницу и триггерим обновление данных
    // Компоненты могут подписаться на refreshTrigger через effect и обновить свои данные
    if (currentUrl.includes('/subscribed-wallets') ||
        currentUrl.includes('/monitoring-page') ||
        currentUrl.includes('/transactions') ||
        currentUrl.includes('/reports') ||
        currentUrl.includes('/add-wallet')) {
      // Триггерим обновление через signal для компонентов, которые подписаны
      this.refreshTrigger.set(Date.now());
    }
    // Для других страниц (payment, transaction-report, reports с id)
    // данные обычно специфичны и не требуют общего обновления
  }

  private _startUserSocket(): void {
    this._userSocketService.connect({
      apiUrl: environment.apiUrl,
      authTelegramData: localStorage.getItem('authTelegramData'),
      xAccountId: this.xAccountId
    });
    this._isUserSocketStarted = true;
  }

  public _stopUserSocket(): void {
    this._userSocketService.disconnect();
    this._isUserSocketStarted = false;
  }

  private _restartUserSocket(): void {
    this._stopUserSocket();
    this._startUserSocket();
  }

  private _initUserSocketHandlers(): void {
    if (!this._userSocketMessageSubscription) {
      this._userSocketMessageSubscription = this._userSocketService.message$.subscribe((rawData: string): void => {
        const user = this._extractUserFromSocketMessage(rawData);
        if (user) {
          this._handleUserData(user);
        }
      });
    }

    if (!this._userSocketErrorSubscription) {
      this._userSocketErrorSubscription = this._userSocketService.error$.subscribe((): void => {
        // Ошибки сокета обрабатываются в сервисе через auto-reconnect.
      });
    }
  }

  private _extractUserFromSocketMessage(rawData: string): AccountDto | null {
    try {
      const parsedData = JSON.parse(rawData) as AccountDto | { data?: AccountDto };
      if (this._isAccountDto(parsedData)) {
        return parsedData;
      }

      if (parsedData && typeof parsedData === 'object' && parsedData.data && this._isAccountDto(parsedData.data)) {
        return parsedData.data;
      }

      return null;
    } catch {
      return null;
    }
  }

  private _isAccountDto(value: unknown): value is AccountDto {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return 'uuid' in value && 'user' in value;
  }
}
